import React, { useEffect, useRef, useState, useMemo } from "react";

export default function SelectScenarios({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  variant = "default",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [dropdownUp, setDropdownUp] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  // Soporta opciones planas y agrupadas (optgroup)
  const normalized = useMemo(() => {
    if (!Array.isArray(options)) return [];
    // Si detecta grupos, los mantiene
    if (options.some((opt) => Array.isArray(opt.options))) {
      return options
        .filter(Boolean)
        .map((group) => ({
          label: group.label,
          options: (group.options || []).map((opt) =>
            typeof opt === "string" || typeof opt === "number"
              ? { value: String(opt), label: String(opt), description: "" }
              : {
                  value: String(opt.value ?? opt.label ?? ""),
                  label: String(opt.label ?? opt.value ?? ""),
                  description: String(opt.description ?? opt.desc ?? ""),
                }
          ),
        }));
    }
    // Si no hay grupos, normaliza plano
    return options
      .filter(Boolean)
      .map((opt) => {
        if (typeof opt === "string" || typeof opt === "number") {
          return { value: String(opt), label: String(opt), description: "" };
        }
        return {
          value: String(opt.value ?? opt.label ?? ""),
          label: String(opt.label ?? opt.value ?? ""),
          description: String(opt.description ?? opt.desc ?? ""),
        };
      })
      .filter((o) => o.value !== undefined && o.value !== null);
  }, [options]);

  // Buscar opción seleccionada (en plano o grupos)
  let selectedLabel = "";
  if (Array.isArray(normalized) && normalized.length && normalized[0].options) {
    for (const group of normalized) {
      const found = group.options.find((o) => String(o.value) === String(value));
      if (found) {
        selectedLabel = found.label;
        break;
      }
    }
  } else {
    const selected = normalized.find((o) => String(o.value) === String(value));
    selectedLabel = selected?.label || "";
  }

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const availableBottom = window.innerHeight - rect.bottom;
    const dropdownHeight = 240; // max-h-60 ~ 240px

    // Determinar si el dropdown debe estar arriba o abajo
    if (availableBottom < dropdownHeight && rect.top > dropdownHeight) {
      setDropdownUp(true);
    } else {
      setDropdownUp(false);
    }
  }, [open]);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  const handlePick = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  const isEthiopia = variant === "ethiopia";

  const buttonCls = isEthiopia
    ? `
      w-full relative
      rounded-xl border border-white/65 bg-white/45 backdrop-blur-sm
      px-3 py-2.5 pr-10
      text-left text-[14px] text-slate-900
      shadow-sm outline-none transition-all duration-200
      ${
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:bg-white/65 hover:border-[#C49A1F]/50"
      }
    `
    : `
      w-full relative
      rounded-xl border border-slate-200 bg-slate-50
      px-3 py-2 pr-10
      text-left text-[14px] text-slate-900
      shadow-sm outline-none
      ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-300"}
    `;

  const dropdownCls = isEthiopia
    ? "z-[9999] mt-1 max-h-60 overflow-y-auto rounded-xl border border-white/60 bg-white backdrop-blur-md shadow-[0_16px_36px_rgba(15,23,42,0.14)]"
    : "bg-white border border-[#F2F2F2] rounded-lg shadow-lg z-[9999] max-h-60 overflow-y-auto mt-1";

  return (
    <div className={`relative w-full ${className}`} ref={ref}>
      {label && (
        <label className="text-[12px] text-[#686873] block pb-1">
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={buttonCls}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? "" : "text-[#A7A7A7]"}>
          {selectedLabel || placeholder}
        </span>

        <span
          className={`
            pointer-events-none
            absolute right-3 top-1/2 -translate-y-1/2
            w-2 h-2 border border-[#5B5F67] border-t-0 border-l-0 rotate-45
            transition-transform
            ${open ? "rotate-[225deg] translate-y-[-20%]" : ""}
          `}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: dropdownUp ? "auto" : "100%",
            bottom: dropdownUp ? "100%" : "auto",
            left: 0,
            right: 0,
          }}
          className={dropdownCls}
        >
          {normalized.length === 0 && (
            <div className="px-4 py-2 text-[14px] text-[#98999A]">
              No options available
            </div>
          )}

          {/* Soporte para grupos */}
          {Array.isArray(normalized) && normalized.length && normalized[0].options
            ? normalized.map((group) => (
                <div key={group.label} className="py-1">
                  <div className="px-4 py-3 text-sm font-bold text-[#1E5B4F] uppercase tracking-wide bg-[#F0F5F0] rounded-t-md">
                    {group.label}
                  </div>
                  {group.options.map((opt) => {
                    const isSelected = String(value) === String(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handlePick(opt.value)}
                        title={opt.description || undefined}
                        className={`
                          w-full rounded-none px-4 py-2 text-[15px] text-left transition-colors
                          ${
                            isSelected
                              ? "bg-[#1E5B4F]/90 text-white"
                              : "text-[#101010] hover:bg-[#1E5B4F]/20"
                          }
                        `}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span>{opt.label}</span>
                          {opt.description ? (
                            <span className="text-xs text-slate-500">i</span>
                          ) : null}
                        </div>
                        {opt.description ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {opt.description}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ))
            : normalized.map((opt) => {
                const isSelected = String(value) === String(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handlePick(opt.value)}
                    title={opt.description || undefined}
                    className={`
                      w-full rounded-none px-4 py-2 text-[15px] text-left transition-colors
                      ${
                        isSelected
                          ? isEthiopia
                            ? "bg-[#1E5B4F]/90 text-white"
                            : "bg-[#F0F5F0] text-[#0F0F0F]"
                          : "text-[#101010]"
                      }
                      ${
                        isEthiopia
                          ? isSelected
                            ? "hover:bg-[#1E5B4F]"
                            : "hover:bg-[#1E5B4F]/20"
                          : "hover:bg-[#F7F9FA]"
                      }
                    `}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span>{opt.label}</span>
                      {opt.description ? (
                        <span className="text-xs text-slate-500">i</span>
                      ) : null}
                    </div>
                    {opt.description ? (
                      <div className="mt-1 text-xs text-slate-500">
                        {opt.description}
                      </div>
                    ) : null}
                  </button>
                );
              })}
        </div>
      )}
    </div>
  );
}